import React, { useEffect, useState } from 'react';
import './search-service-centre.scss';
import DealerSearchDropdown from '../../../dealer-search-dropdown/dealer-search-dropdown';
import { useDealerStep, useOSBStep } from '../../../../../../hooks/owners-osb';
import { FMButton } from '@own/fds-react';
import { useOSBContentFragment } from '../../../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import { useHistory } from 'react-router-dom';
import { buildNavigationUrl } from '../../../../owners-osb/osb-utils';
import { OsbPathContactDetailsStep } from '../../../osb-view-controller';
import { useLocationAddress } from '../../../../../../hooks/osb-v3/use-location-address/use-location-address';

interface SearchServiceCentreProps {
    onPreferredDealerClick: () => void;
    hasPreferredDealer: boolean;
}

const SearchServiceCentre: React.FC<SearchServiceCentreProps> = ({
    onPreferredDealerClick,
    hasPreferredDealer,
}) => {
    const { setOSBDealerStepPayload, osbDealerStep } = useDealerStep();
    const { osbStep } = useOSBStep();
    const osbContentData =
        useOSBContentFragment('common/osb-v3', 'service-centre-step') || {};
    const history = useHistory();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const {
        address,
        error,
        isLoading,
        getAddress,
        clearError,
    } = useLocationAddress({
        enableHighAccuracy: true,
        timeout: 10000,
    });
    useEffect(() => {
        if (address) {
            setOSBDealerStepPayload({
                selectedDealerName: address,
            });
            console.log('Selected Value - ', address);
        }
    }, [address]);
    useEffect(() => {
        if (error) {
            switch (error.type) {
                case 'PermissionDenied':
                    setErrorMessage(
                        String(osbContentData?.locationPermissionDenied ?? '')
                    );
                    break;
                case 'PositionUnavailable':
                    setErrorMessage(
                        String(
                            osbContentData?.locationPositionUnavailable ?? ''
                        )
                    );
                    break;
                case 'Timeout':
                    setErrorMessage(
                        String(osbContentData.locationTimeout ?? '')
                    );
                    break;
                default:
                    setErrorMessage(
                        String(osbContentData.locationUnknown ?? '')
                    );
                    break;
            }
        } else {
            setErrorMessage(null);
        }
    }, [error, osbContentData]);
    const handleUseMyLocationClick = () => {
        setOSBDealerStepPayload({
            selectedDealerName: '',
        });
        setErrorMessage(null);
        getAddress();
    };
    const handleDealerInputChange = () => {
        if (error) {
            clearError();
        }
    };
    return (
        <div
            data-testid="search-service-centre-wrapper"
            className="osb-v3-search-service-centre-wrapper"
        >
            <div
                data-testid="search-service-centre-heading"
                className="osb-v3-search-service-centre-heading"
            >
                {osbContentData?.fordServiceCentreTitle}
            </div>
            <div
                data-testid="search-service-centre-container"
                className="osb-v3-search-service-centre"
                aria-busy={isLoading}
            >
                <div data-testid="search-title" className="osb-v3-search-title">
                    {osbContentData?.searchByLocationOrServiceCentreTitle}
                </div>
                {isLoading ? (
                    <>
                        <div className="osb-v3-search-input">
                            <div className="osb-v3-skeleton osb-v3-skeleton-input" />
                        </div>
                        <div className="osb-v3-skeleton osb-v3-skeleton-link" />
                        <div className="osb-v3-search-service-centre-button-container">
                            <div className="osb-v3-skeleton osb-v3-skeleton-button" />
                            {hasPreferredDealer && (
                                <div className="osb-v3-skeleton osb-v3-skeleton-button" />
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div
                            data-testid="search-input"
                            className="osb-v3-search-input"
                        >
                            <label className="osb-v3-search-input-label">
                                {
                                    osbContentData?.enterTownOrDealerNamePlaceholder
                                }
                            </label>
                            <DealerSearchDropdown
                                data-testid="dealer-search-dropdown"
                                onSelect={(selectedValue: string): void => {
                                    console.log(
                                        'Selected Value - ',
                                        selectedValue
                                    );
                                }}
                                onInputChange={handleDealerInputChange}
                                locationLabel={`${osbContentData?.locationDropdownCheckboxLabel}`}
                                serviceCentreLabel={`${osbContentData?.serviceCentreDropdownCheckboxLabel}`}
                                selectedValue={osbDealerStep.selectedDealerName}
                            />
                        </div>
                        <FMButton
                            data-testid="use-my-location-button"
                            className="osb-v3-use-my-location-link"
                            label={osbContentData?.useMyLocationLinkText}
                            type="tertiary"
                            onClick={handleUseMyLocationClick}
                            disabled={isLoading}
                        />
                        <div
                            className={`osb-v3-location-error-message ${
                                errorMessage ? 'is-visible' : ''
                            }`}
                            aria-live="assertive"
                        >
                            {errorMessage}
                        </div>
                        <div
                            data-testid="button-container"
                            className="osb-v3-search-service-centre-button-container"
                        >
                            <FMButton
                                data-testid="continue-button"
                                className="osb-v3-search-service-centre-continue-button"
                                label={
                                    osbContentData?.continueButtonDealerSearchLabel
                                }
                                theme="light"
                                type="primary"
                                onClick={() => {
                                    history.push(
                                        buildNavigationUrl(
                                            OsbPathContactDetailsStep(),
                                            osbStep.UrlQueryParams
                                        )
                                    );
                                }}
                            />
                            {hasPreferredDealer && (
                                <FMButton
                                    data-testid="preferred-dealer-button"
                                    className="osb-v3-search-service-centre-button"
                                    onClick={onPreferredDealerClick}
                                    label={
                                        osbContentData?.useMyPreferredServiceCentreButtonLabel
                                    }
                                    theme="light"
                                    type="secondary"
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchServiceCentre;
